"use client";

import { useEffect, useState } from "react";
import { getPostHogClientAsync } from "@/lib/posthog/client.tsx";

export function useFeatureFlag(flagKey: string): {
  enabled: boolean;
  loading: boolean;
} {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    async function setupFeatureFlag() {
      try {
        const posthog = await getPostHogClientAsync();

        if (!mounted) return;

        if (!posthog) {
          console.log(
            "🔍 PostHog not available, defaulting feature flag to false:",
            flagKey,
          );
          setEnabled(false);
          setLoading(false);
          return;
        }

        // Get initial value
        const value = posthog.isFeatureEnabled(flagKey);

        if (!mounted) return;

        setEnabled(value === true);
        setLoading(false);

        // Listen for changes - only set up if still mounted
        if (mounted) {
          unsubscribe = posthog.onFeatureFlags(() => {
            if (!mounted) return;
            const newValue = posthog.isFeatureEnabled(flagKey);
            // console.log('🔄 Feature Flag Updated:', flagKey, '=', newValue);
            setEnabled(newValue === true);
            setLoading(false);
          });
        }
      } catch (error) {
        console.error("❌ Error setting up feature flag:", flagKey, error);
        if (mounted) {
          setEnabled(false);
          setLoading(false);
        }
      }
    }

    setupFeatureFlag();

    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [flagKey]);

  return { enabled, loading };
}
